// @flow
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedDate } from 'react-intl';
import classnames from 'classnames';
import { Button, buttonTypes, buttonSizes } from 'moods-react/Button';
import { Collapse } from 'moods-react/Collapse';
import { Tooltip, tooltipPosition, tooltipCaretDirections, tooltipTypes } from 'moods-react/Tooltip';
import { Spinner, spinnerSizes } from 'moods-react/Spinner';
import { IconUiPreview, IconUiEdit } from 'moods-react/Icons';
import ApprovalDialog from '../../common/dialog/approve/ApprovalDialogComponent';
import ImageLoader from '../../common/image-loader/ImageLoaderComponent';
import type { Approval } from '../../../api/types/Approvals';
import approvalMessages from '../../../data/approvals';
import { APPROVAL_STATUS } from '../../../api/types/Approvals';
import AnalyticsClient from '../../../clients/AnalyticsClient';
import approvalEvents from '../../../analytics/events/approvals';

import './Approval.scss';

type Props = {
  approval: Approval & {
    isHidden: boolean,
    isLoading: boolean,
  },
  updateApprovalStatus: Function,
  showOverlay: Function,
  closeOverlay: Function,
};

type Context = {
  intl: Object,
};

export const COLLAPSE_DELAY = 500;

class ApprovalComponent extends React.Component<Props, Context> {
  static contextTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }),
  };

  constructor(props: Props) {
    super();

    this.state = {
      collapse: props.approval.isHidden || false,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.approval.isHidden) {
      this.setState({ collapse: true });
    }
  }

  approveDialog(approval: Object, requestedBy: String): Element {
    const approvalAction = () => {
      this.props.updateApprovalStatus(approval, APPROVAL_STATUS.approved);
      AnalyticsClient.trackEvent(approvalEvents.adminApproveProductConfirmationClick());
    };

    return (
      <ApprovalDialog
        thumbnailImageUrl={approval.printProduct.images.thumb}
        thumbnailAltText={approval.printProduct.productSize.name}
        title={this.context.intl.formatMessage(approvalMessages.approveDialogTitle)}
        description={this.context.intl.formatMessage(approvalMessages.approveDialogDescription, {
          employeeName: requestedBy,
          productType: approval.printProduct.productType,
        })}
        buttonText={this.context.intl.formatMessage(approvalMessages.approveDialogButtonApprove)}
        actions={{
          approvalAction,
          closeOverlay: this.props.closeOverlay,
        }}
      />
    );
  }

  declineDialog(approval: Object, requestedBy: String): Element {
    const approvalAction = (reason) => {
      this.props.updateApprovalStatus(approval, APPROVAL_STATUS.declined, reason);
      AnalyticsClient.trackEvent(approvalEvents.adminDeclineProductConfirmationClick(!!reason));
    };

    return (
      <ApprovalDialog
        thumbnailImageUrl={approval.printProduct.images.thumb}
        thumbnailAltText={approval.printProduct.productSize.name}
        title={this.context.intl.formatMessage(approvalMessages.declineDialogTitle)}
        description={this.context.intl.formatMessage(approvalMessages.declineDialogDescription, {
          employeeName: requestedBy,
        })}
        buttonText={this.context.intl.formatMessage(approvalMessages.approveDialogButtonDecline)}
        isReasonFieldEnabled
        actions={{
          approvalAction,
          closeOverlay: this.props.closeOverlay,
        }}
      />
    );
  }

  handleClick(dialog: Node): void {
    this.props.showOverlay({
      overlayName: ApprovalDialog.displayName,
      overlayContent: dialog,
    });
  }

  approveProductClick = () => {
    const { approval } = this.props;
    this.handleClick(this.approveDialog(approval, approval.requestedBy));
    AnalyticsClient.trackEvent(approvalEvents.adminApproveProductClick());
  };

  declineProductClick = () => {
    const { approval } = this.props;
    this.handleClick(this.declineDialog(approval, approval.requestedBy));
    AnalyticsClient.trackEvent(approvalEvents.adminDeclineProductClick());
  };

  render() {
    const { approval } = this.props;
    const { printProduct, approvalCreatedDate, requestedBy, isLoading, isHidden } = approval;
    const { name, productType, paperOption } = printProduct;
    const sizeName = printProduct.productSize.name;
    const classes = classnames('approval-component', 'u-margin-vertical-s', 'fade-then-hide', {
      'is-visible': !isHidden,
    });

    const overlayClasses = classnames('approval-component__overlay', 'overlay-screen', 'u-position-absolute', {
      'is-visible': isLoading,
    });

    return (
      <Collapse isOpen={!this.state.collapse} transitionTime={COLLAPSE_DELAY}>
        <div className={classes}>
          <div className={overlayClasses} data-approval-overlay>
            <Spinner size={spinnerSizes.LARGE} />
          </div>
          <div className="approval-component__thumbnail u-padding-s u-padding-l@medium">
            <ImageLoader image={approval.printProduct.images.thumb} altText={approval.printProduct.productSize.name} />
            <div className="approval-component__thumbnail-ctas u-padding-s">
              <Button
                data-approval-preview-link-mobile
                isDisc
                icon={IconUiPreview}
                href={approval.printProduct.endpoints.downloadPdfUrl}
                type={buttonTypes.STANDALONE_WHITE}
              />
            </div>
          </div>
          <div className="approval-component__product-container u-padding-xs u-padding-l@medium">
            <div className="approval-component__product-info">
              <div
                className="approval-component__name u-fontFamily-primaryMedium u-marginBottom-xxs"
                data-approval-name
              >
                {name}
              </div>
              <div className="approval-component__requested-by" data-approval-requested-by>
                <span className="u-fontFamily-secondaryMedium">
                  {this.context.intl.formatMessage(approvalMessages.approvalsDashboardRequestedByLabel)}:{' '}
                </span>
                {requestedBy} {this.context.intl.formatMessage(approvalMessages.approvalsDashboardOnText)}{' '}
                <FormattedDate value={approvalCreatedDate} day="2-digit" month="short" year="numeric" />
              </div>
              <div className="approval-component__product" data-approval-product>
                <span className="u-fontFamily-secondaryMedium">
                  {this.context.intl.formatMessage(approvalMessages.approvalsDashboardProductLabel)}:{' '}
                </span>
                {sizeName} {productType}
              </div>
              <div className="approval-component__paper" data-approval-paper>
                <span className="u-fontFamily-secondaryMedium">
                  {this.context.intl.formatMessage(approvalMessages.approvalsDashboardPaperLabel)}:{' '}
                </span>
                {paperOption}
              </div>
            </div>
            <div className="approval-component__ctas u-marginTop-l">
              <div className="approval-component__ctas--left">
                {/* Preview Button */}
                <div className="approval-component__preview-link">
                  <Button
                    data-approval-preview-link-desktop
                    isDisc
                    icon={IconUiPreview}
                    href={approval.printProduct.endpoints.downloadPdfUrl}
                    type={buttonTypes.TRANSPARENT_DARK}
                    tooltip={
                      <Tooltip
                        position={tooltipPosition.BELOW}
                        caretDirection={tooltipCaretDirections.TOP}
                        type={tooltipTypes.BLACK}
                      >
                        {this.context.intl.formatMessage(approvalMessages.approvalsDashboardPreviewTooltipText)}
                      </Tooltip>
                    }
                  />
                </div>
                {/* Edit Button */}
                <div className="approval-component__edit-link">
                  <Button
                    data-approval-edit-link-desktop
                    isDisc
                    icon={IconUiEdit}
                    href={approval.printProduct.endpoints.pixelEditUrl}
                    type={buttonTypes.TRANSPARENT_DARK}
                    onClick={() => AnalyticsClient.trackEvent(approvalEvents.adminEditAndApproveProductClick())}
                    tooltip={
                      <Tooltip
                        position={tooltipPosition.BELOW}
                        caretDirection={tooltipCaretDirections.TOP}
                        type={tooltipTypes.BLACK}
                      >
                        {this.context.intl.formatMessage(approvalMessages.approvalsDashboardEditTooltipText)}
                      </Tooltip>
                    }
                  />
                </div>
              </div>
              <div className="approval-component__ctas--right">
                <Button
                  size={buttonSizes.MEDIUM}
                  type={buttonTypes.SECONDARY}
                  onClick={this.declineProductClick}
                  className="approval-component__decline-button"
                  data-approval-decline-button
                >
                  {this.context.intl.formatMessage(approvalMessages.approvalsDashboardDeclineButtonText)}
                </Button>
                <Button
                  size={buttonSizes.MEDIUM}
                  type={buttonTypes.PRIMARY}
                  onClick={this.approveProductClick}
                  className="approval-component__approve-button"
                  data-approval-approve-button
                >
                  {this.context.intl.formatMessage(approvalMessages.approvalsDashboardApproveButtonText)}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Collapse>
    );
  }
}

export default ApprovalComponent;
